/**
 * Turns a `CompiledSkillPlanV3` into the absolute, integer-tick release
 * schedule one cast follows.
 *
 * This module owns the arithmetic — where stage-relative offsets land on the
 * cast's clock — and nothing else. It allocates no identities, touches no
 * world state, spawns nothing, and is a pure function of the plan plus the
 * cast's release tick. That is what lets the offline simulator and the
 * authoritative world agree on ordering without sharing anything but this.
 *
 * `TickBucketScheduler` remains the authoritative timing owner at runtime;
 * this produces what to hand it.
 */
import type { Delivery } from '../types.js';
import type { StageTimingV1 } from '../skill-engine/composite-types.js';
import type { CompiledSkillPlanV3, CompiledStageV3 } from '../skill-engine/composite-compiler.js';

/** The listener's attack-source filter. Absent and ANY include follow-up attacks. */
export function stageEventSourceMatches(filter: { readonly source?: 'PRIMARY' | 'ANY' }, followUp: boolean): boolean {
  return filter.source !== 'PRIMARY' || !followUp;
}

/**
 * The stable structural identity of one attack inside one cast:
 * `(executionId, stageId, volleyIndex, memberIndex|null)`, as the canonical
 * contract ratifies. `memberIndex === null` marks a shared union, where
 * several footprints resolve as one attack.
 *
 * Distinct from the runtime's numeric `attackId`, which links into the
 * existing event/causality system. This key is for diagnosis and replay: it
 * says *which* attack, stably, across runs.
 */
export interface AttackKeyV1 {
  readonly executionId: number;
  readonly stageId: string;
  readonly volleyIndex: number;
  readonly memberIndex: number | null;
}

export function formatAttackKey(key: AttackKeyV1): string {
  return `${key.executionId}:${key.stageId}:${key.volleyIndex}:${key.memberIndex ?? 'union'}`;
}

export interface ScheduledAttackUnit {
  readonly key: AttackKeyV1;
  /** Absolute tick this unit releases on. */
  readonly atTick: number;
  readonly stageOrder: number;
  readonly stage: CompiledStageV3;
  /** Index into `stage.units`, so the caller can reach the transforms
   * without this module copying them. */
  readonly unitIndex: number;
  /**
   * Set when this unit is released at a fraction of a reference stage whose
   * length depends on the outcome (a projectile's flight ends when it hits):
   * `atTick` is the fraction of the PLANNED length, and the unit comes out
   * early when the reference has ended before then.
   */
  readonly endsWithStageId?: string;
}

export type StageScheduleResult =
  | {
      readonly ok: true;
      /** The units whose release tick is known at cast start (of a chain stage, the first member of each volley). */
      readonly units: readonly ScheduledAttackUnit[];
      /** Stages released by an event instead; their units wait for it. */
      readonly eventStages: readonly CompiledStageV3[];
      /** Stages released at the carrier of a skill status when it is applied, ticks or ends; their units wait for it. */
      readonly statusStages: readonly CompiledStageV3[];
    }
  | { readonly ok: false; readonly code: string; readonly message: string };

/**
 * The tick a stage's own clock starts on, and how long that clock runs.
 *
 * Every stage has a length: a sweep runs its duration, a projectile its
 * planned flight, a zone or orbit its lifetime, anything else one tick. A
 * stage anchored to a fraction of a sibling reads that length from here.
 */
interface StageClock {
  readonly startTick: number;
  readonly durationTicks: number;
}

/** How long this stage's own attack runs, in ticks. */
export function stageDurationTicks(stage: CompiledStageV3, ticksPerSecond: number): number {
  return attackDurationTicks(stage.runtimePlan.runtimeSkill.delivery, ticksPerSecond,
    stage.runtimePlan.runtimeSkill.weaponContactApplication?.motion, stage.orbit);
}

/** The scheduler's duration rule, also used before a stage has numeric effects. */
export function attackDurationTicks(delivery: Delivery, ticksPerSecond: number,
  motion?: { readonly kind: string; readonly durationS?: number }, orbit?: { readonly lifetimeS: number }): number {
  const toTicks = (seconds: number): number => Math.max(1, Math.round(seconds * ticksPerSecond));
  if (orbit !== undefined) return toTicks(orbit.lifetimeS);
  if (motion?.kind === 'WEAPON_SWEEP' && motion.durationS !== undefined && motion.durationS > 0) return toTicks(motion.durationS);
  if (delivery.type === 'projectile' && delivery.speedMps > 0) return toTicks(delivery.rangeM / delivery.speedMps);
  if (delivery.type === 'ground_aoe' && delivery.durationS !== undefined && delivery.durationS > 0) return toTicks(delivery.durationS);
  return 1;
}

/** One stage's start on the cast clock; event stages wait for their runtime event. */
export function stageClockStart(timing: StageTimingV1, clocks: ReadonlyMap<string, StageClock>, releaseTick: number): number | undefined {
  switch (timing.kind) {
    case 'AT_RELEASE': return releaseTick;
    case 'AFTER_RELEASE': return releaseTick + timing.offsetTicks;
    case 'AT_STAGE_FRACTION': {
      const reference = clocks.get(timing.referenceStageId);
      return reference === undefined ? undefined : reference.startTick + Math.round(reference.durationTicks * timing.fraction);
    }
    case 'ON_STAGE_EVENT':
    case 'ON_STATUS': return undefined;
  }
}

/** True when this stage's attack can end before its planned length. */
function lengthDependsOnOutcome(stage: CompiledStageV3): boolean {
  return attackLengthDependsOnOutcome(stage.runtimePlan.runtimeSkill.delivery, stage.orbit !== undefined);
}

/**
 * Compute the absolute release tick of every attack unit in a cast.
 *
 * Stages are resolved in authored order, which is exactly why a fraction
 * anchor may only name an earlier stage: by the time a stage is resolved,
 * every stage it could reference already has a known clock. No cycle is
 * representable, so none needs detecting here.
 */
export function scheduleCompositeCast(
  plan: CompiledSkillPlanV3,
  releaseTick: number,
  ticksPerSecond: number,
): StageScheduleResult {
  if (!Number.isInteger(releaseTick) || releaseTick < 0) {
    return { ok: false, code: 'COMPOSITE_RELEASE_TICK_INVALID', message: 'releaseTick must be a non-negative integer' };
  }
  if (!Number.isFinite(ticksPerSecond) || ticksPerSecond <= 0) {
    return { ok: false, code: 'COMPOSITE_TICK_RATE_INVALID', message: 'ticksPerSecond must be finite and positive' };
  }

  const clocks = new Map<string, StageClock>();
  const stagesById = new Map<string, CompiledStageV3>();
  const scheduled: ScheduledAttackUnit[] = [];
  const eventStages: CompiledStageV3[] = [];
  const statusStages: CompiledStageV3[] = [];

  for (const stage of plan.stages) {
    stagesById.set(stage.stageId, stage);
    if (stage.timing.kind === 'ON_STAGE_EVENT') { eventStages.push(stage); continue; }
    if (stage.timing.kind === 'ON_STATUS') { statusStages.push(stage); continue; }
    const startTick = stageClockStart(stage.timing, clocks, releaseTick);
    if (startTick === undefined) return {
      ok: false, code: 'COMPOSITE_STAGE_TIMING_UNKNOWN_REFERENCE',
      message: `stage ${stage.stageId} has no resolved clock`,
    };

    clocks.set(stage.stageId, { startTick, durationTicks: stageDurationTicks(stage, ticksPerSecond) });
    const endsWithStage = stage.timing.kind === 'AT_STAGE_FRACTION' ? stagesById.get(stage.timing.referenceStageId) : undefined;
    const endsWithStageId = endsWithStage !== undefined && lengthDependsOnOutcome(endsWithStage) ? endsWithStage.stageId : undefined;

    stage.units.forEach((unit, unitIndex) => {
      // A chain's later members are released at the enemies it strikes, not at a tick known at cast start.
      if (stage.chain !== undefined && unit.memberIndex !== 0) return;
      scheduled.push({
        key: {
          // Filled by the executor, which owns the cast's execution identity.
          executionId: -1,
          stageId: stage.stageId,
          volleyIndex: unit.volleyIndex,
          memberIndex: unit.memberIndex,
        },
        atTick: startTick + unit.releaseOffsetTicks,
        stageOrder: stage.stageOrder,
        stage,
        unitIndex,
        ...(endsWithStageId === undefined ? {} : { endsWithStageId }),
      });
    });
  }

  // Deterministic total order. Ties at one tick break by authored stage
  // order, then volley, then member — never by insertion or by map iteration,
  // so two runs of the same cast resolve attacks in the same sequence.
  scheduled.sort(
    (a, b) =>
      a.atTick - b.atTick ||
      a.stageOrder - b.stageOrder ||
      a.key.volleyIndex - b.key.volleyIndex ||
      (a.key.memberIndex ?? -1) - (b.key.memberIndex ?? -1),
  );

  return { ok: true, units: scheduled, eventStages, statusStages };
}

/** Bind a schedule to the cast's execution identity. Separate from
 * `scheduleCompositeCast` so the arithmetic stays pure and testable without
 * inventing an execution id. */
export function bindExecutionId(
  units: readonly ScheduledAttackUnit[],
  executionId: number,
): readonly ScheduledAttackUnit[] {
  return units.map((unit) => ({ ...unit, key: { ...unit.key, executionId } }));
}

/** The units that release on the cast's own release tick, i.e. immediately. */
export function immediateUnits(
  units: readonly ScheduledAttackUnit[],
  releaseTick: number,
): readonly ScheduledAttackUnit[] {
  return units.filter((unit) => unit.atTick <= releaseTick);
}

/** The units that release later and therefore need scheduling and are
 * cancellable until they fire. */
export function deferredUnits(
  units: readonly ScheduledAttackUnit[],
  releaseTick: number,
): readonly ScheduledAttackUnit[] {
  return units.filter((unit) => unit.atTick > releaseTick);
}

/** The runtime event queue's release clock, before a unit enters the scheduler. */
export function eventStageReleaseTick(eventTick: number, delayTicks = 0, unitOffsetTicks = 0, queued = false): number {
  const planned = eventTick + delayTicks + unitOffsetTicks;
  return queued ? Math.max(eventTick + 1, planned) : planned;
}

export function attackLengthDependsOnOutcome(delivery: Delivery, orbit = false): boolean {
  return !orbit && delivery.type === 'projectile';
}

/** A pending fraction-relative unit releases early when its reference ends first. */
export function stageUnitReleaseTick(plannedTick: number, referenceEndTick?: number): number {
  return Math.min(plannedTick, referenceEndTick ?? Infinity);
}
